import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: (...args: unknown[]) => void;
  }
}
function initMetaPixel(pixelId: string) {
  if (window.fbq) return;
  const n: any = window.fbq = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  };
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [] as unknown[][];
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
}
import { XCircle, AlertTriangle, Magnet, CheckCircle2, BookOpen, Users, Video, MessageSquare, Settings, TrendingUp, Filter, ChevronDown, ShieldCheck } from 'lucide-react';
import { Footer } from './components/Footer';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} data-id="element-0">
    {children}
  </motion.div>;
const Divider = () => <div className="w-full flex justify-center py-12" data-id="element-1">
    <div className="h-px w-24 bg-gold opacity-50" data-id="element-2"></div>
  </div>;
const CTAButton = ({
  text = 'QUIERO EL FILTRO MAGNÉTICO AHORA',
  scrollToPrice = false
}: {
  text?: string;
  scrollToPrice?: boolean;
}) => <motion.a href={scrollToPrice ? '#pricing' : 'https://www.prospectflow.store/cart/47660891177218:1'} target={scrollToPrice ? undefined : '_blank'} rel={scrollToPrice ? undefined : 'noopener noreferrer'} onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
  if (scrollToPrice) {
    e.preventDefault();
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  } else if (window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_name: 'El Filtro Magnético',
      content_type: 'product',
      value: 6.39,
      currency: 'USD'
    });
  }
}} whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} className="w-full md:w-auto px-10 py-5 bg-gold hover:bg-gold-hover text-navy-900 font-bold text-lg md:text-xl rounded-lg shadow-[0_0_25px_rgba(201,168,76,0.5)] hover:shadow-[0_0_40px_rgba(201,168,76,0.7)] transition-all ring-2 ring-gold/60 ring-offset-2 ring-offset-navy-900 flex flex-col items-center justify-center gap-1 mx-auto cursor-pointer no-underline" data-id="element-3">
    <span data-id="element-4">{text}</span>
    <span className="text-xs font-medium opacity-80" data-id="element-5">
      Pago seguro garantizado • Acceso inmediato
    </span>
  </motion.a>;
export function App() {
  useEffect(() => {
    initMetaPixel('1293149622738671');
  }, []);
  return <div className="min-h-screen bg-navy-900 text-offwhite font-inter selection:bg-gold selection:text-navy-900" data-id="element-6">
      {/* HERO SECTION */}
      <section className="pt-20 pb-16 px-6 relative overflow-hidden" data-id="element-7">
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-8">
          <FadeIn data-id="element-9">
            <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-gold uppercase tracking-wide mb-8" data-id="element-10">
              Para Networkers
            </h2>
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-11">
              Deja de ser el "amigo pesado" que todos bloquean en WhatsApp y
              conviértete en un{' '}
              <span className="text-gold italic" data-id="element-12">
                imán de socios calificados.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-offwhite/80 mb-10 leading-relaxed font-light" data-id="element-13">
              El sistema de "Prospección Inversa" para Networkers que desean
              libertad real, sin listas de 100, sin mensajes en frío y sin morir
              en el intento.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-14">
            <div className="relative mx-auto max-w-2xl mb-12" data-id="element-15">
              <div className="absolute inset-0 bg-gold/20 blur-3xl rounded-full" data-id="element-16"></div>
              <img src="https://cdn.magicpatterns.com/uploads/bZDPfM83jwhjvAQS58SRzV/heylloyd-image-1776136810829.png" alt="El Filtro Magnético Mockup" className="relative z-10 w-full h-auto rounded-xl shadow-2xl border border-white/5" data-id="element-17" />
            </div>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-18">
            <CTAButton scrollToPrice data-id="element-19" />
          </FadeIn>
        </div>
      </section>

      {/* THE HOOK */}
      <section className="py-16 px-6 bg-navy-800" data-id="element-20">
        <div className="max-w-3xl mx-auto" data-id="element-21">
          <FadeIn data-id="element-22">
            <p className="text-xl md:text-2xl font-playfair leading-relaxed mb-6" data-id="element-23">
              <span className="text-gold font-bold" data-id="element-24">Admitámoslo:</span> te
              dijeron que Facebook era para "conectar", pero lo único que has
              logrado es que tus amigos de la secundaria dejen de ver tus
              historias para no recibir otro mensaje de "hola, ¿cómo estás?,
              tengo un proyecto...".
            </p>
            <p className="text-lg text-offwhite/80 leading-relaxed mb-6" data-id="element-25">
              Estás cansado. Tienes un gran producto y una gran oportunidad,
              pero te sientes como un vendedor de seguros desesperado
              persiguiendo a gente que no quiere lo que tú tienes.
            </p>
            <p className="text-xl font-medium text-gold" data-id="element-26">
              El problema no es tu negocio, es tu sistema de filtración.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 px-6" data-id="element-27">
        <div className="max-w-3xl mx-auto" data-id="element-28">
          <FadeIn data-id="element-29">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-10 text-center" data-id="element-30">
              El error del "Suicidio Social"
            </h2>
            <p className="text-lg text-offwhite/80 mb-8 text-center" data-id="element-31">
              La mayoría de los networkers cometen estos errores mortales:
            </p>

            <div className="space-y-6 mb-10" data-id="element-32">
              {['Publican fotos de batidos y logos de empresas que nadie pidió ver.', 'Envían guiones genéricos que gritan "SPAM" a kilómetros.', 'Tratan a su familia como prospectos, destruyendo relaciones en el proceso.'].map((item, i) => <div key={i} className="flex items-start gap-4 bg-navy-800/50 p-6 rounded-lg border border-red-900/30" data-id="element-33">
                  <XCircle className="w-6 h-6 text-red-400 shrink-0 mt-1" data-id="element-34" />
                  <p className="text-lg" data-id="element-35">{item}</p>
                </div>)}
            </div>

            <div className="bg-red-900/20 border border-red-500/20 p-6 rounded-lg text-center" data-id="element-36">
              <p className="text-xl font-playfair italic text-red-200" data-id="element-37">
                El resultado: Te quedas sin contactos, sin socios y con la
                autoestima por los suelos.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHY IT FAILED */}
      <section className="py-20 px-6 bg-navy-800" data-id="element-38">
        <div className="max-w-3xl mx-auto" data-id="element-39">
          <FadeIn data-id="element-40">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-41">
              Por qué lo que intentaste no funcionó
            </h2>

            <div className="grid gap-8" data-id="element-42">
              {[{
              title: 'La lista de 100 es finita',
              desc: 'Una vez que se acaba (y se acaba rápido), tu negocio muere si no sabes atraer desconocidos.'
            }, {
              title: 'Vender antes de filtrar',
              desc: 'Tratas de convencer a gente que no quiere emprender, en lugar de atraer a los que ya están buscando.'
            }, {
              title: 'Falta de postura profesional',
              desc: 'Al perseguir, pierdes toda la autoridad para liderar a alguien más.'
            }].map((item, i) => <div key={i} className="flex gap-6" data-id="element-43">
                  <div className="w-12 h-12 rounded-full bg-navy-900 flex items-center justify-center shrink-0 border border-gold/20" data-id="element-44">
                    <AlertTriangle className="w-5 h-5 text-gold" data-id="element-45" />
                  </div>
                  <div data-id="element-46">
                    <h3 className="text-xl font-bold mb-2 text-gold" data-id="element-47">
                      {item.title}
                    </h3>
                    <p className="text-offwhite/80 leading-relaxed" data-id="element-48">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* UNIQUE MECHANISM */}
      <section className="py-24 px-6 relative overflow-hidden" data-id="element-49">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-gold/5 blur-[120px] pointer-events-none" data-id="element-50"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-51">
          <FadeIn data-id="element-52">
            <Magnet className="w-16 h-16 text-gold mx-auto mb-6" data-id="element-53" />
            <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-8" data-id="element-54">
              El Mecanismo Único: <br data-id="element-55" />
              <span className="text-gold italic" data-id="element-56">El Filtro Magnético</span>
            </h2>
            <p className="text-xl leading-relaxed mb-6" data-id="element-57">
              He diseñado un sistema llamado <strong data-id="element-58">Inbound Networking</strong>
              . No se trata de salir a cazar; se trata de preparar el jardín
              para que las mariposas vengan a ti.
            </p>
            <p className="text-lg text-offwhite/80 leading-relaxed" data-id="element-59">
              Con El Filtro Magnético, dejas de ser el cazador para ser el
              seleccionado. Usamos psicología de atracción y contenido de
              polaridad para que el prospecto levante la mano y te pida permiso
              para conocer tu proyecto.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 px-6 bg-navy-800" data-id="element-60">
        <div className="max-w-3xl mx-auto" data-id="element-61">
          <FadeIn data-id="element-62">
            <div className="text-center mb-12" data-id="element-63">
              <p className="text-gold uppercase tracking-widest text-sm font-bold mb-3" data-id="element-64">
                Todo incluido
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold" data-id="element-65">
                Qué vas a recibir hoy
              </h2>
            </div>

            <div className="space-y-5" data-id="element-66">
              {[{
              icon: BookOpen,
              title: 'El eBook Estratégico',
              desc: 'El paso a paso para configurar tu imán de prospectos.'
            }, {
              icon: Users,
              title: 'Guía de los 5 Avatares',
              desc: 'Para que sepas exactamente a quién le hablas y dejes de perder tiempo con curiosos sin dinero.'
            }, {
              icon: Video,
              title: 'El Sistema 3-S',
              desc: '3 tipos de contenido semanal (Reels/Carruseles) con guiones listos para usar.'
            }, {
              icon: MessageSquare,
              title: 'Scripts de Filtro de Hierro',
              desc: 'Qué decir exactamente en el DM para calificar a un socio en 3 minutos.'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-navy-900 p-6 md:p-7 rounded-xl border border-gold/20 border-l-4 border-l-gold flex flex-col md:flex-row gap-5 items-start md:items-center shadow-[0_0_20px_rgba(201,168,76,0.05)]" data-id="element-67">
                  <div className="relative shrink-0" data-id="element-68">
                    <div className="w-16 h-16 rounded-xl bg-gold/10 flex items-center justify-center" data-id="element-69">
                      <item.icon className="w-8 h-8 text-gold" data-id="element-70" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center" data-id="element-71">
                      <CheckCircle2 className="w-4 h-4 text-white" data-id="element-72" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-73">
                    <h3 className="text-xl font-bold mb-1 text-offwhite" data-id="element-74">
                      {item.title}
                    </h3>
                    <p className="text-offwhite/70 leading-relaxed" data-id="element-75">
                      {item.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHO IS IT FOR / NOT FOR */}
      <section className="py-20 px-6" data-id="element-76">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-77">
          <FadeIn data-id="element-78">
            <div className="bg-navy-800 p-8 rounded-2xl border border-green-500/20 h-full" data-id="element-79">
              <h3 className="font-playfair text-2xl font-bold mb-6 text-green-400 flex items-center gap-3" data-id="element-80">
                <CheckCircle2 className="w-6 h-6" data-id="element-81" /> Para quién ES esto
              </h3>
              <ul className="space-y-5" data-id="element-82">
                {['Networkers estancados que ya quemaron su lista de contactos conocidos.', "Personas que aman su empresa pero odian los métodos de 'prospección vieja escuela'.", 'Emprendedores que tienen poco tiempo y necesitan que su Instagram trabaje por ellos.'].map((text, i) => <li key={i} className="flex gap-3 text-offwhite/80" data-id="element-83">
                    <span className="text-green-400 mt-1" data-id="element-84">•</span>
                    <span data-id="element-85">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-86">
            <div className="bg-navy-800 p-8 rounded-2xl border border-red-500/20 h-full" data-id="element-87">
              <h3 className="font-playfair text-2xl font-bold mb-6 text-red-400 flex items-center gap-3" data-id="element-88">
                <XCircle className="w-6 h-6" data-id="element-89" /> Para quién NO es
              </h3>
              <ul className="space-y-5" data-id="element-90">
                {["Personas que buscan 'el botón mágico' para hacerse ricos sin trabajar.", 'Quienes no están dispuestos a mostrar su cara o su voz de forma profesional.', 'Networkers que prefieren seguir haciendo spam a pesar de no tener resultados.'].map((text, i) => <li key={i} className="flex gap-3 text-offwhite/80" data-id="element-91">
                    <span className="text-red-400 mt-1" data-id="element-92">•</span>
                    <span data-id="element-93">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-6 bg-navy-800" data-id="element-94">
        <div className="max-w-3xl mx-auto" data-id="element-95">
          <FadeIn data-id="element-96">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-16 text-center" data-id="element-97">
              Cómo funciona en 3 pasos
            </h2>

            <div className="relative" data-id="element-98">
              <div className="absolute left-8 top-0 bottom-0 w-px bg-gold/20 hidden md:block" data-id="element-99"></div>
              <div className="space-y-12" data-id="element-100">
                {[{
                icon: Settings,
                title: 'Paso 1: Configura',
                desc: 'Ajustas tu biografía y perfil en 15 minutos siguiendo mis plantillas.'
              }, {
                icon: TrendingUp,
                title: 'Paso 2: Atrae',
                desc: 'Publicas los contenidos estratégicos que generan curiosidad, no rechazo.'
              }, {
                icon: Filter,
                title: 'Paso 3: Filtra',
                desc: 'Usas los guiones de calificación para decidir con quién quieres hablar y con quién no.'
              }].map((step, i) => <div key={i} className="relative flex flex-col md:flex-row gap-6 md:gap-10 items-start" data-id="element-101">
                    <div className="w-16 h-16 rounded-full bg-navy-900 border-2 border-gold flex items-center justify-center shrink-0 relative z-10 shadow-[0_0_15px_rgba(201,168,76,0.3)]" data-id="element-102">
                      <step.icon className="w-7 h-7 text-gold" data-id="element-103" />
                    </div>
                    <div className="pt-3" data-id="element-104">
                      <h3 className="text-2xl font-bold mb-3 text-gold" data-id="element-105">
                        {step.title}
                      </h3>
                      <p className="text-lg text-offwhite/80" data-id="element-106">{step.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 7 DAYS TRANSFORMATION */}
      <section className="py-24 px-6 relative" data-id="element-107">
        <div className="max-w-3xl mx-auto text-center" data-id="element-108">
          <FadeIn data-id="element-109">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-8" data-id="element-110">
              Lo que cambia en 7 días
            </h2>
            <div className="bg-gold/10 border border-gold/20 p-8 md:p-12 rounded-2xl relative" data-id="element-111">
              <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-6xl text-gold opacity-50 font-playfair" data-id="element-112">
                "
              </span>
              <p className="text-xl md:text-2xl font-playfair leading-relaxed italic text-offwhite" data-id="element-113">
                Imagina despertar el lunes y, en lugar de tener que pensar a
                quién vas a "molestar" hoy, tienes 3 mensajes en tu bandeja de
                entrada diciendo:{' '}
                <span className="text-gold font-bold" data-id="element-114">
                  "Vi tu publicación, me interesa tu sistema de trabajo,
                  ¿podemos hablar?"
                </span>
                .
              </p>
              <p className="mt-8 text-lg font-bold uppercase tracking-wider text-gold/80" data-id="element-115">
                Esa es la diferencia entre perseguir y atraer.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 bg-navy-800" data-id="element-116">
        <div className="max-w-3xl mx-auto" data-id="element-117">
          <FadeIn data-id="element-118">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-119">
              Preguntas Frecuentes
            </h2>

            <div className="space-y-6" data-id="element-120">
              {[{
              q: '¿Necesito muchos seguidores?',
              a: 'No. El sistema funciona incluso con cuentas nuevas porque se basa en la calidad del mensaje, no en la cantidad de fans.'
            }, {
              q: '¿Funciona para cualquier empresa de MLM?',
              a: 'Sí. Es un sistema de marketing aplicable a cualquier producto o plan de compensación.'
            }, {
              q: '¿Qué pasa si soy tímido?',
              a: 'Te doy guiones exactos para que no tengas que improvisar nada.'
            }].map((faq, i) => <div key={i} className="bg-navy-900 p-6 rounded-xl border border-white/5" data-id="element-121">
                  <h3 className="text-xl font-bold mb-3 flex items-start gap-3" data-id="element-122">
                    <ChevronDown className="w-6 h-6 text-gold shrink-0" data-id="element-123" />
                    {faq.q}
                  </h3>
                  <p className="text-offwhite/70 pl-9" data-id="element-124">{faq.a}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER & PRICING */}
      <section id="pricing" className="py-24 px-6 relative overflow-hidden" data-id="element-125">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gold/10 via-navy-900 to-navy-900 pointer-events-none" data-id="element-126"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-127">
          <FadeIn data-id="element-128">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-6" data-id="element-129">
              Obtén el pack completo de <br data-id="element-130" />
              <span className="text-gold italic" data-id="element-131">El Filtro Magnético</span> hoy
              mismo.
            </h2>
            <p className="text-xl text-offwhite/80 mb-12" data-id="element-132">
              Olvida las mentorías de $500 que solo te dan teoría.
            </p>

            <div className="bg-navy-800 border-2 border-gold rounded-3xl p-8 md:p-12 shadow-[0_0_40px_rgba(201,168,76,0.15)] max-w-xl mx-auto mb-10" data-id="element-133">
              <div className="inline-block bg-red-500 text-white text-sm font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6 animate-pulse" data-id="element-134">
                🔥 Oferta de Lanzamiento
              </div>
              <p className="text-lg uppercase tracking-widest text-gold font-bold mb-4" data-id="element-135">
                Acceso de por vida
              </p>
              <div className="flex justify-center items-center gap-4 mb-2" data-id="element-136">
                <span className="text-2xl text-offwhite/40 line-through" data-id="element-137">
                  $27 USD
                </span>
              </div>
              <div className="flex justify-center items-start gap-1 mb-4" data-id="element-138">
                <span className="text-3xl font-bold text-gold mt-2" data-id="element-139">$</span>
                <span className="text-8xl font-bold text-white tracking-tighter" data-id="element-140">
                  6,39
                </span>
                <span className="text-xl font-bold text-gold mt-auto mb-3" data-id="element-141">
                  USD
                </span>
              </div>
              <p className="text-offwhite/60 mb-2" data-id="element-142">
                Un solo pago. Sin suscripciones ocultas.
              </p>
              <p className="text-red-400 font-semibold text-sm mb-8" data-id="element-143">
                ⚠️ Este precio es solo por lanzamiento. Pronto volverá a $27.
              </p>

              <CTAButton data-id="element-144" />

              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-offwhite/60" data-id="element-145">
                <ShieldCheck className="w-4 h-4 text-green-400" data-id="element-146" />
                <span data-id="element-147">Pago 100% seguro y encriptado</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <Footer data-id="element-148" />
    </div>;
}